import { createContext, useContext, useMemo, useState } from "react";
import {
    computeAffordability as computeAffordabilityCore,
} from "@/homeiq/engine/affordabilityCore";
import {
    computeLoanAmortisation,
    computePrepaymentImpact,
} from "@/homeiq/engine/loanCore";
import { computeMarketOutputs } from "@/homeiq/engine/marketCore";
import { computeInvestmentOutputs } from "@/homeiq/engine/investmentCore";
import { computeHomeIQComposite } from "@/homeiq/engine/homeIQScore";
import { MARKETS } from "@/homeiq/services/placeholders";
import { inferProfileFromRent } from "@/homeiq/landing/rentProfile";

const HomeIQContext = createContext(null);

// Investment engine assumptions — shared by the assessment and the landing
// Reference Profile pipelines. Kept at module scope so there's one canonical
// value and both consumers stay in lock-step.
const INVESTMENT_ASSUMPTIONS = {
    equityCAGR: 12,            // % p.a.
    rentEscalationPct: 5,      // % p.a.
    maintPerSqftMo: 6,         // ₹/sqft/mo
    maintGstPct: 18,           // % GST on maintenance
    maintEscalationPct: 5,     // % p.a. maintenance step-up
    depositMonths: 6,
};

// Default loan rate centralised here for the whole app.
// Product decision (Feb 2026): use realistic Bengaluru benchmark of 8.75%.
const DEFAULT_RATE = 8.75;

// Lazy initialiser — computes the at-FOIR EMI for the seeded affordability
// inputs so the EMI Planner slider starts AT the user's eligible EMI
// (anything they drag above it immediately registers "Debt-Free Earlier By").
function seedTargetEMI() {
    const seed = computeAffordabilityCore({
        salary: 200000, obligations: 0, bankRule: 55,
        downPayment: 2000000, tenure: 20, rate: DEFAULT_RATE,
        targetEMI: 0,
    });
    return Math.max(10000, Math.round(seed.emi_on_eligible_loan));
}

export function HomeIQProvider({ children }) {
    // Rent (landing) — starts EMPTY. User must explicitly enter a value.
    // Range: ₹0 to ₹5,00,000 (slider + direct number entry).
    const [rent, setRent] = useState(null);

    // Shell navigation
    const [activeSection, setActiveSection] = useState("home");
    const [vastuStep, setVastuStep] = useState("landing"); // landing|processing|results
    const [legalModal, setLegalModal] = useState(null); // null|disclaimer|terms|privacy

    // Affordability inputs (placeholders)
    const [salary, setSalary] = useState(200000);
    const [bankRule, setBankRule] = useState(55);
    const [obligations, setObligations] = useState(0);
    const [downPayment, setDownPayment] = useState(2000000);
    const [tenure, setTenure] = useState(20);
    const [rate, setRate] = useState(DEFAULT_RATE);
    const [targetEMI, setTargetEMI] = useState(seedTargetEMI);

    // Loan · prepayment simulator inputs (shared across Affordability + Loan tabs)
    const [partPaymentLump, setPartPaymentLump] = useState(200000);
    const [partPaymentFrequency, setPartPaymentFrequency] = useState("Annual"); // "One-time" | "Annual" | "Monthly extra"

    // Scroll target — a tab can request a section be scrolled into view after navigation.
    const [scrollTargetId, setScrollTargetId] = useState(null);

    // Market filters (placeholders)
    const [segment, setSegment] = useState("Mid");
    const [sba, setSba] = useState(1500);
    const [zone, setZone] = useState("All");
    const [sortBy, setSortBy] = useState("Affordable");
    const [wealthYear, setWealthYear] = useState(10);
    const [spotlightMarket, setSpotlightMarket] = useState(null);
    const [pinnedMarketId, setPinnedMarketId] = useState(null); // user-pinned market for Tier 4 CAGR
    const [budgetFilter, setBudgetFilter] = useState("All"); // All | Within | Stretch | Beyond
    // Read-only carry-over from Affordability (Phase 1 placeholder = ₹99 L).
    // Tier 1 — derived live below; these are now aliases.

    // Investment Insights — Phase 1 placeholder assumptions (no calculations yet).
    const assumptions = {
        rentEscalationPct: 8,
        maintenancePct: 1.5,
        equityCAGR: 12,
        fdRate: 7,
        goldCAGR: 8,
        rateDriftBpsPerYear: 25,
    };
    const [costOfDelayHorizon, setCostOfDelayHorizon] = useState(2); // 1 | 2 | 3 | 5

    // Tier 6 — Vastu pipeline state.
    // We retain ORIGINAL extraction separately from the (possibly user-edited)
    // confirmed plan so we can later diff misreads vs corrections and improve
    // prompts. `vastuFinal` carries the scored report once the user confirms.
    const [vastuExtraction, setVastuExtraction] = useState(null);
    // { session_id, confidence, confidence_basis, extracted_plan, raw_model_output }
    const [vastuConfirmedPlan, setVastuConfirmedPlan] = useState(null);
    // dict mirroring extracted_plan after user edits
    const [vastuFinal, setVastuFinal] = useState(null);
    // ScoreResponse shape

    // Recommendation confidence — placeholder, Phase 2 wires to composite score.
    const recommendationConfidence = "Moderate"; // High | Moderate | Low

    // Tier 1 — live Affordability calculation engine (D1–D19, S_aff).
    const affordabilityOutputs = useMemo(
        () => computeAffordabilityCore({ salary, obligations, bankRule, downPayment, tenure, rate, targetEMI }),
        [salary, obligations, bankRule, downPayment, tenure, rate, targetEMI]
    );
    // Aliases for existing consumers (Loan, Market, Investment tabs)
    const affordableBudget = affordabilityOutputs.max_affordable_property;
    const eligibleLoan = affordabilityOutputs.eligible_loan;
    const placeholderEMI = Math.round(affordabilityOutputs.emi_on_eligible_loan);

    // Tier 2 — live Loan amortisation engine (schedule, milestones, S_loan).
    // Consumes Tier 1 outputs only; never recomputes affordability.
    const loanOutputs = useMemo(
        () => {
            const base = computeLoanAmortisation({
                principal: affordabilityOutputs.eligible_loan,
                rate,
                tenure,
                emi: affordabilityOutputs.emi_on_eligible_loan,
                salary,
            });
            const prepayment = computePrepaymentImpact({
                principal: affordabilityOutputs.eligible_loan,
                rate,
                tenure,
                emi: affordabilityOutputs.emi_on_eligible_loan,
                lump: partPaymentLump,
                frequency: partPaymentFrequency,
            });
            return { ...base, prepayment };
        },
        [
            affordabilityOutputs.eligible_loan,
            affordabilityOutputs.emi_on_eligible_loan,
            rate, tenure, salary,
            partPaymentLump, partPaymentFrequency,
        ]
    );

    // Tier 3 — live Market budget wiring (status, gaps, S_mkt).
    // Consumes Tier 1 D6 (max_affordable_property) as the budget anchor.
    const marketOutputs = useMemo(
        () => computeMarketOutputs({
            markets: MARKETS,
            sba,
            wealthYear,
            budget: affordabilityOutputs.max_affordable_property,
        }),
        [sba, wealthYear, affordabilityOutputs.max_affordable_property]
    );

    // Tier 4 — live Investment / Buy-vs-Rent engine.
    // PropertyCAGR resolves to: pinned market → best within-budget → 6.5% fallback.
    // Horizon binds to `wealthYear` so Market + Investment tabs share the same projection window.
    const investmentOutputs = useMemo(
        () => {
            const pinnedMarket = pinnedMarketId
                ? marketOutputs.decorated.find((m) => m.id === pinnedMarketId)
                : null;
            let source = "fallback";
            let chosen = null;
            let resolvedCAGR = 6.5;
            if (pinnedMarket) {
                source = "pinned";
                chosen = pinnedMarket;
                resolvedCAGR = pinnedMarket.growth;
            } else if (marketOutputs.best_within) {
                source = "best_within";
                chosen = marketOutputs.best_within;
                resolvedCAGR = marketOutputs.best_within.growth;
            }
            const marketChoice = { source, market: chosen };
            const affordabilitySnapshot = {
                foirUsagePct: affordabilityOutputs.foir_usage_pct,
                downRatio: affordabilityOutputs.eligible_loan > 0
                    ? downPayment / affordabilityOutputs.eligible_loan
                    : 0,
                segment: affordabilityOutputs.segment,
            };
            return computeInvestmentOutputs({
                downPayment,
                emi: affordabilityOutputs.emi_on_eligible_loan,
                tenure,
                eligible_loan: affordabilityOutputs.eligible_loan,
                property_value_t0: affordabilityOutputs.max_affordable_property,
                schedule: loanOutputs.schedule,
                rent,
                sba,
                propertyCAGR: resolvedCAGR,
                horizon: wealthYear,
                marketChoice,
                affordabilitySnapshot,
                assumptions: INVESTMENT_ASSUMPTIONS,
            });
        },
        [
            downPayment,
            tenure,
            sba,
            rent,
            wealthYear,
            pinnedMarketId,
            marketOutputs.decorated,
            marketOutputs.best_within,
            affordabilityOutputs.emi_on_eligible_loan,
            affordabilityOutputs.eligible_loan,
            affordabilityOutputs.max_affordable_property,
            affordabilityOutputs.foir_usage_pct,
            affordabilityOutputs.segment,
            loanOutputs.schedule,
        ]
    );

    // Tier 7 — Composite HomeIQ Score (Vastu excluded for now).
    // Reads each tier's live sub-score. When `investmentOutputs.blocked`
    // (no rent), investment_score is null and weights re-normalise.
    const homeIQScore = useMemo(
        () => computeHomeIQComposite({
            affordability: affordabilityOutputs.affordability_score,
            loan: loanOutputs.loan_score,
            market: marketOutputs.market_score,
            investment: investmentOutputs.investment_score,
        }),
        [
            affordabilityOutputs.affordability_score,
            loanOutputs.loan_score,
            marketOutputs.market_score,
            investmentOutputs.investment_score,
        ]
    );

    // ────────────────────────────────────────────────────────────
    // Landing Reference Profile pipeline.
    //
    // The landing page has only one input (rent) but needs every card to
    // respond to it. We infer a plausible buyer profile from rent (see
    // /app/frontend/src/homeiq/landing/landing-profile.md) and run the
    // SAME Core engines that power the assessment — just with different
    // inputs. Landing components read from `landing*` outputs; assessment
    // components continue reading `affordabilityOutputs` / `loanOutputs`
    // / etc. from the user-entered inputs above. There is only ever one
    // implementation of `computeAffordability`, `computeHomeIQComposite`,
    // etc. — the difference is which inputs go in.
    //
    // When `rent` is null/0 all landing outputs are null so the landing
    // components can render their empty-state placeholders.
    // ────────────────────────────────────────────────────────────
    const landingProfile = useMemo(() => inferProfileFromRent(rent), [rent]);

    const landingAffordabilityOutputs = useMemo(
        () => (landingProfile ? computeAffordabilityCore(landingProfile) : null),
        [landingProfile]
    );

    const landingLoanOutputs = useMemo(() => {
        if (!landingProfile || !landingAffordabilityOutputs) return null;
        return computeLoanAmortisation({
            principal: landingAffordabilityOutputs.eligible_loan,
            rate:      landingProfile.rate,
            tenure:    landingProfile.tenure,
            emi:       landingAffordabilityOutputs.emi_on_eligible_loan,
            salary:    landingProfile.salary,
        });
    }, [landingProfile, landingAffordabilityOutputs]);

    const landingMarketOutputs = useMemo(() => {
        if (!landingProfile || !landingAffordabilityOutputs) return null;
        return computeMarketOutputs({
            markets: MARKETS,
            sba: landingProfile.sba,
            wealthYear,
            budget: landingAffordabilityOutputs.max_affordable_property,
        });
    }, [landingProfile, landingAffordabilityOutputs, wealthYear]);

    const landingInvestmentOutputs = useMemo(() => {
        if (!landingProfile || !landingAffordabilityOutputs || !landingLoanOutputs || !landingMarketOutputs) {
            return null;
        }
        const best = landingMarketOutputs.best_within;
        const resolvedCAGR = best ? best.growth : 6.5;
        const marketChoice = {
            source: best ? "best_within" : "fallback",
            market: best,
        };
        const affordabilitySnapshot = {
            foirUsagePct: landingAffordabilityOutputs.foir_usage_pct,
            downRatio: landingAffordabilityOutputs.eligible_loan > 0
                ? landingProfile.downPayment / landingAffordabilityOutputs.eligible_loan
                : 0,
            segment: landingAffordabilityOutputs.segment,
        };
        return computeInvestmentOutputs({
            downPayment:       landingProfile.downPayment,
            emi:               landingAffordabilityOutputs.emi_on_eligible_loan,
            tenure:            landingProfile.tenure,
            eligible_loan:     landingAffordabilityOutputs.eligible_loan,
            property_value_t0: landingAffordabilityOutputs.max_affordable_property,
            schedule:          landingLoanOutputs.schedule,
            rent,
            sba:               landingProfile.sba,
            propertyCAGR:      resolvedCAGR,
            horizon:           wealthYear,
            marketChoice,
            affordabilitySnapshot,
            assumptions:       INVESTMENT_ASSUMPTIONS,
        });
    }, [
        rent,
        landingAffordabilityOutputs,
        landingLoanOutputs,
        landingMarketOutputs,
        landingProfile,
        wealthYear,
    ]);

    const landingHomeIQScore = useMemo(() => {
        if (!landingAffordabilityOutputs) {
            return { score: 0, verdict: "Pending", pillars: homeIQScore.pillars.map(p => ({ ...p, score: 0, included: false })) };
        }
        return computeHomeIQComposite({
            affordability: landingAffordabilityOutputs.affordability_score,
            loan: landingLoanOutputs?.loan_score ?? null,
            market: landingMarketOutputs?.market_score ?? null,
            investment: landingInvestmentOutputs?.investment_score ?? null,
        });
    }, [
        landingAffordabilityOutputs,
        landingLoanOutputs,
        landingMarketOutputs,
        landingInvestmentOutputs,
        homeIQScore.pillars,
    ]);

    // One-way seed from landing → assessment. Fires exactly once, when the
    // user proceeds from the landing into the Affordability tab. After
    // that the two experiences are independent and the user can edit
    // salary/downPayment freely on the assessment side.
    const [assessmentSeededFromLanding, setAssessmentSeededFromLanding] = useState(false);
    const seedAssessmentFromLanding = () => {
        if (assessmentSeededFromLanding) return;
        if (!landingProfile) return;
        setSalary(landingProfile.salary);
        setDownPayment(landingProfile.downPayment);
        setAssessmentSeededFromLanding(true);
    };

    const value = {
        rent, setRent,
        activeSection, setActiveSection,
        vastuStep, setVastuStep,
        legalModal, setLegalModal,
        salary, setSalary,
        bankRule, setBankRule,
        obligations, setObligations,
        downPayment, setDownPayment,
        tenure, setTenure,
        rate, setRate,
        targetEMI, setTargetEMI,
        segment, setSegment,
        sba, setSba,
        zone, setZone,
        sortBy, setSortBy,
        wealthYear, setWealthYear,
        spotlightMarket, setSpotlightMarket,
        budgetFilter, setBudgetFilter,
        affordableBudget,
        eligibleLoan,
        placeholderEMI,
        assumptions,
        costOfDelayHorizon, setCostOfDelayHorizon,
        recommendationConfidence,
        homeIQScore,
        // Landing Reference Profile pipeline — used only by landing
        // components. See /homeiq/landing/landing-profile.md.
        landingProfile,
        landingAffordabilityOutputs,
        landingLoanOutputs,
        landingMarketOutputs,
        landingInvestmentOutputs,
        landingHomeIQScore,
        seedAssessmentFromLanding,
        assessmentSeededFromLanding,
        affordabilityOutputs,
        loanOutputs,
        marketOutputs,
        investmentOutputs,
        partPaymentLump, setPartPaymentLump,
        partPaymentFrequency, setPartPaymentFrequency,
        scrollTargetId, setScrollTargetId,
        pinnedMarketId, setPinnedMarketId,
        vastuExtraction, setVastuExtraction,
        vastuConfirmedPlan, setVastuConfirmedPlan,
        vastuFinal, setVastuFinal,
    };

    return (
        <HomeIQContext.Provider value={value}>{children}</HomeIQContext.Provider>
    );
}

export function useHomeIQ() {
    const ctx = useContext(HomeIQContext);
    if (!ctx) throw new Error("useHomeIQ must be used within HomeIQProvider");
    return ctx;
}
